import {
  AiTextGeneratorFormValues,
  geminiFileds,
} from '../AiConfigs/constants';
import {
  Box,
  Chip,
  FormControl,
  FormControlLabel,
  FormLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { PrettoSlider } from '../../assets/styles/styles';
import { useAuthStore } from '../../store/storeAuth';
import { useState } from 'react';

type Props = {
  watch: AiTextGeneratorFormValues;
  onChange: (event: any) => void;
  imageSystem?: boolean;
  systems?: any[];
};

const modelInputBoxStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: '4px',
  mt: '8px',
};

const Gemini = ({ systems, onChange, watch, imageSystem = false }: Props) => {
  const { user } = useAuthStore(state => state);
  const [isAutoSetting, setIsAutoSetting] = useState(watch.autoToken);

  const geminiSystem = systems?.filter(
    (item: any) => item.system === 'gemini',
  )[0];
  const textModels = (geminiSystem?.models || []).filter(
    (m: any) => m.type === 'Text' && !m.isDeprecated,
  );

  return (
    <>
      <Box mt={'4px'}>
        <Typography
          fontSize={'14px'}
          fontWeight={500}
          sx={{
            mb: '4px',
            color: user?.theme === 'dark' ? '#D0D5DD' : 'black',
          }}
          variant="body1"
        >
          Model
        </Typography>
        <FormControl sx={{ width: '100%' }}>
          <Select
            className="nodrag"
            name="aiModel"
            fullWidth
            size="small"
            value={watch.aiModel}
            sx={{
              height: 'unset',
              bgcolor: user?.theme === 'dark' ? '#1D2939' : '#F9FAFB',
              color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
            }}
            onChange={onChange}
            MenuProps={{
              style: {
                maxHeight: '700px',
              },
            }}
          >
            {textModels.map((opt: any, index: number) => (
              <MenuItem
                className="nodrag"
                key={opt.model + index}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'start',
                  alignItems: 'start',
                  padding: '16px',
                }}
                value={opt.model}
                disabled={opt.enabled === false || opt.disabledByCompany}
              >
                <Box display={'flex'} alignItems={'center'} gap={'8px'}>
                  <Typography
                    mb={'4px'}
                    textTransform={'capitalize'}
                    variant="body2"
                    color={user?.theme === 'dark' ? '#D0D5DD' : 'black'}
                  >
                    {opt.displayName || opt.model}
                  </Typography>
                  {opt.disabledByCompany && (
                    <Typography fontSize={'12px'} color="red">
                      (disabled by admin)
                    </Typography>
                  )}
                </Box>
                <Box
                  display={'flex'}
                  gap={'8px'}
                  alignItems={'center'}
                  flexWrap={'wrap'}
                >
                  {opt?.tags?.map((tag: any, index: number) => {
                    const tagLabel = typeof tag === 'string' ? tag : tag.label;
                    const tagTooltip =
                      typeof tag === 'string' ? '' : tag.tooltip;

                    return (
                      <Tooltip
                        key={tagLabel + index}
                        title={tagTooltip}
                        arrow
                        placement="top"
                      >
                        <Chip
                          sx={{
                            borderRadius: '4px',
                            fontSize: '12px',
                            fontWeight: 600,
                            border: `1px solid ${
                              user?.theme === 'dark' ? '#475467' : '#EAECF0'
                            }`,
                            color: user?.theme === 'dark' ? '#D0D5DD' : 'black',
                          }}
                          label={tagLabel}
                        />
                      </Tooltip>
                    );
                  })}
                </Box>
                <Typography
                  maxWidth={'570px'}
                  variant="body2"
                  sx={{
                    color: '#667085',
                    mt: '8px',
                    whiteSpace: 'pre-wrap',
                    textAlign: 'start',
                  }}
                >
                  {opt.description}
                </Typography>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      {geminiFileds.map(opt => {
        const optName = opt.name;
        if (opt.name === 'geminiMaxOutputTokens') {
          return (
            <Box
              sx={{
                ...modelInputBoxStyle,
                alignItems: 'center',
                mt: '20px',
              }}
              key={optName}
            >
              <FormControlLabel
                control={
                  <Switch
                    onChange={e => {
                      onChange({
                        target: {
                          name: 'autoToken',
                          value: !watch.autoToken,
                        },
                      });

                      setIsAutoSetting(!watch.autoToken);
                    }}
                    name="autoToken"
                    checked={watch.autoToken}
                    sx={{
                      margin: 0,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '5px',
                    }}
                  />
                }
                label="Auto setting"
              />
              <FormControl>
                <TextField
                  id="demo-simple-select"
                  type={opt.type}
                  onChange={onChange}
                  value={isAutoSetting ? 0 : watch.geminiMaxOutputTokens}
                  disabled={isAutoSetting}
                  className="nodrag"
                  label={'Output Token Max'}
                  name={opt.name}
                  InputProps={{
                    style: {
                      color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
                    },
                  }}
                  sx={{
                    width: '290px',
                    color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
                    bgcolor: user?.theme === 'dark' ? '#1D2939' : '#F9FAFB',
                    border: `2px solid ${
                      user?.theme === 'dark' ? '#475467' : '#EAECF0'
                    }`,
                  }}
                />
              </FormControl>
            </Box>
          );
        }

        return (
          <Box key={opt.name} style={modelInputBoxStyle}>
            <FormLabel
              htmlFor="tokens"
              sx={{
                bgcolor: user?.theme === 'dark' ? '#1D2939' : '#F9FAFB',
                color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
              }}
            >
              {opt.label}
            </FormLabel>
            <Box display={'flex'} alignItems={'center'} gap={'10px'}>
              {opt.name !== 'geminiMaxOutputTokens' && (
                <PrettoSlider
                  step={Number(opt.step)}
                  min={Number(opt.min)}
                  name={opt.name}
                  value={Number(
                    watch[optName as keyof AiTextGeneratorFormValues],
                  )}
                  max={Number(opt.max)}
                  style={{ marginLeft: '8px', width: '350px' }}
                  onChange={e => {
                    onChange(e);
                  }}
                  className="nodrag"
                />
              )}

              <TextField
                type={'number'}
                value={watch[optName as keyof AiTextGeneratorFormValues]}
                name={opt.name}
                onChange={e => {
                  onChange(e);
                }}
                InputProps={{
                  inputProps: {
                    min: opt.min,
                    max: opt.max,
                    step: opt.step,
                  },
                  style: {
                    color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
                  },
                }}
                style={{ marginLeft: '8px', width: '100px' }}
                className="nodrag"
                sx={{
                  color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
                  bgcolor: user?.theme === 'dark' ? '#1D2939' : '#F9FAFB',
                  border: `2px solid ${
                    user?.theme === 'dark' ? '#475467' : '#EAECF0'
                  }`,
                }}
              />
            </Box>
          </Box>
        );
      })}
    </>
  );
};

export default Gemini;
